import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDraft } from "../../contexts/DraftContext";
import { useAuth } from "../../contexts/AuthContext";
import PositionBadge from "../PositionBadge";
import PlayerPoolWithDrawer from "./PlayerPoolWithDrawer";
import RightPanel from "./RightPanel";
import { Plus, Minus } from "lucide-react";

// Uses timerVersion as primary dep - fires even when reset value is identical
function useTimer(timerRemaining, timerVersion) {
  const [timeLeft, setTimeLeft] = useState(timerRemaining || 0);
  const ref = useRef(null);
  const trRef = useRef(timerRemaining);
  trRef.current = timerRemaining;

  useEffect(() => {
    const remaining = trRef.current || 0;
    setTimeLeft(remaining);
    clearInterval(ref.current);
    if (remaining > 0) {
      ref.current = setInterval(() => setTimeLeft(p => Math.max(0, p - 1)), 1000);
    }
    return () => clearInterval(ref.current);
  }, [timerVersion]);

  return timeLeft;
}

export default function MobilePaddle({ leagueId, league }) {
  const { user } = useAuth();
  const { draftState, auctioneerMessage, placeBid, nominate, nomination, soldInfo } = useDraft();
  const [tab, setTab] = useState("bid");
  const [bidAmount, setBidAmount] = useState(1);
  const [winAnim, setWinAnim] = useState(false);
  const prevSaleCount = useRef(0);

  
  const timeLeft = useTimer(draftState?.timer_remaining, draftState?.timer_version);
  const myData = draftState?.managers?.[user?.id];
  const currentBid = nomination?.current_bid || 1;
  const isHighestBidder = nomination?.current_bidder_id === user?.id;
  const state = draftState?.state;

  useEffect(() => { if (nomination) setBidAmount(currentBid + 1); }, [currentBid]);

  useEffect(() => {
    const n = draftState?.completed_sales?.length || 0;
    if (n > prevSaleCount.current) {
      const last = draftState?.completed_sales?.slice(-1)[0];
      if (last?.winner_id === user?.id) { setWinAnim(true); setTimeout(() => setWinAnim(false), 3000); }
    }
    prevSaleCount.current = n;
  }, [draftState?.completed_sales?.length]);

  const timerColor = timeLeft <= 5
    ? "text-[#FF6B00] glow-orange animate-timer-pulse"
    : timeLeft <= 10 ? "text-[#FF6B00]"
    : "text-[#F5F5F5]";

  return (
    <div className="flex flex-col h-full bg-[#111111] relative overflow-hidden">
      {/* Win animation */}
      <AnimatePresence>
        {winAnim && (
          <motion.div className="absolute inset-0 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ background: "rgba(0,200,83,0.12)" }}>
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }} className="text-center">
              <p className="font-display font-black text-6xl text-[#00C853] uppercase">YOU WON!</p>
              <p className="font-body text-[#F5F5F5] text-xl mt-2">{draftState?.completed_sales?.slice(-1)[0]?.player?.name}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top - player info */}
      <div className="bg-[#1C1C1C] border-b border-[#252525] px-4 py-3 shrink-0">
        {nomination ? (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <PositionBadge position={nomination.player?.position} size="lg" />
              <h2 className="font-display font-black text-2xl uppercase text-[#F5F5F5] truncate">{nomination.player?.name}</h2>
            </div>
            <p className="font-body text-xs text-[rgba(255,255,255,0.45)]">{nomination.player?.nfl_team} · {nomination.player?.projected_points?.toFixed(0)} pts</p>
          </div>
        ) : (
          <p className="font-display font-bold text-lg uppercase text-[rgba(255,255,255,0.40)]">
            {state === "NOMINATION" ? "Waiting for nomination..." : state === "PAUSED" ? "Draft Paused" : "Connecting..."}
          </p>
        )}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        {tab === "bid" && (
          <div className="flex flex-col h-full">
            {nomination && (
              <div className="flex-1 flex flex-col items-center justify-center px-4">
                <div className={`font-mono font-black text-5xl mb-2 ${timerColor}`}>
                  {String(Math.floor(timeLeft / 60)).padStart(2, "0")}:{String(timeLeft % 60).padStart(2, "0")}
                </div>
                {/* Current bid - glow when active */}
                <div className={`font-mono font-black text-7xl mb-1 ${currentBid > 1 ? "text-[#FF6B00] glow-orange" : "text-[#F5F5F5]"}`}>${currentBid}</div>
                <p className="font-body text-sm mb-4 text-[rgba(255,255,255,0.45)]">
                  {isHighestBidder ? <span className="text-[#00C853] font-semibold">YOU'RE WINNING!</span> : `${draftState?.managers?.[nomination.current_bidder_id]?.team_name || "..."} leads`}
                </p>
                {(state === "GOING_ONCE" || state === "GOING_TWICE") && (
                  <div className="border border-[#FF6B00]/40 px-4 py-2 mb-4" style={{ background: "rgba(255,107,0,0.06)" }}>
                    <p className={`font-display font-black text-xl uppercase tracking-widest text-[#FF6B00] glow-orange`}>
                      {state === "GOING_ONCE" ? "GOING ONCE!" : "GOING TWICE!"}
                    </p>
                  </div>
                )}
                <p className="font-body text-xs text-[rgba(255,255,255,0.35)]">Budget: <span className="text-[#00C853] font-bold">${myData?.budget || 0}</span></p>
              </div>
            )}
            {nomination && !isHighestBidder && (
              <div className="px-4 pb-4 pt-2 shrink-0">
                <div className="flex items-center gap-3 mb-3">
                  <button onClick={() => setBidAmount(b => Math.max(currentBid + 1, b - 1))} className="w-12 h-12 bg-[#252525] hover:bg-[#2E2E2E] rounded-xl text-[#F5F5F5] font-bold flex items-center justify-center transition-colors"><Minus size={18} /></button>
                  <div className="flex-1 bg-[#1C1C1C] border border-[#252525] rounded-xl text-center py-3">
                    <span className="font-mono font-black text-3xl text-[#F5F5F5]">${bidAmount}</span>
                  </div>
                  <button onClick={() => setBidAmount(b => b + 1)} className="w-12 h-12 bg-[#252525] hover:bg-[#2E2E2E] rounded-xl text-[#F5F5F5] font-bold flex items-center justify-center transition-colors"><Plus size={18} /></button>
                </div>
                <button data-testid="mobile-bid-btn" onClick={() => placeBid(bidAmount)}
                  className="w-full bg-[#00C853] hover:bg-[#00E676] text-[#111111] font-display font-black text-3xl uppercase tracking-wider py-5 rounded-2xl transition-all active:scale-95"
                  style={{ boxShadow: "0 0 30px rgba(0,200,83,0.35)" }}>
                  BID
                </button>
              </div>
            )}
            {isHighestBidder && nomination && (
              <div className="px-4 pb-4 shrink-0">
                <div className="w-full border border-[#00C853]/40 rounded-2xl py-5 text-center" style={{ background: "rgba(0,200,83,0.08)" }}>
                  <p className="font-display font-black text-2xl text-[#00C853] uppercase">You're Winning!</p>
                </div>
              </div>
            )}
          </div>
        )}
        {tab === "analysis" && <div className="h-full overflow-y-auto p-3"><RightPanel draftState={draftState} leagueId={leagueId} user={user} /></div>}
        {tab === "roster"   && <div className="h-full overflow-y-auto p-3"><PlayerPoolWithDrawer draftState={draftState} leagueId={leagueId} /></div>}
      </div>

      {/* Bottom nav */}
      <div className="bg-[#1C1C1C] border-t border-[#252525] flex shrink-0">
        {[{ key: "bid", label: "Bid" }, { key: "analysis", label: "Analysis" }, { key: "roster", label: "Roster" }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`mobile-tab-${t.key}`}
            className={`flex-1 py-3 font-display font-bold text-xs uppercase tracking-wider transition-colors ${tab === t.key ? "text-[#FF6B00] border-t-2 border-[#FF6B00]" : "text-[rgba(255,255,255,0.40)]"}`}>
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}
